import { useTranslation } from "react-i18next";
import { CreateHomeworkAssignmentRequestBody } from "@packages/api/madlenAPI.schemas";
import { useEffect, useState, useRef } from "react";
import { Button, Text } from "@core/index";
import { Input } from "@core/input";
import { DateTimePicker } from "@core/datepicker";

// Extended type to include custom properties needed for the component
interface ExtendedAssignmentRequestBody
  extends CreateHomeworkAssignmentRequestBody {
  hwAssignmentType?: "HOMEWORK" | "QUIZ";
  duration?: number;
}

const CommonPropertiesStep = ({
  assignmentData,
  onChange,
}: {
  assignmentData: ExtendedAssignmentRequestBody;
  onChange: (data: Partial<ExtendedAssignmentRequestBody>) => void;
}) => {
  const { t } = useTranslation();
  const [localAssignmentData, setLocalAssignmentData] =
    useState<ExtendedAssignmentRequestBody>(assignmentData);

  const [durationString, setDurationString] = useState(
    assignmentData.duration?.toString() ?? "",
  );

  // Ref to track if changes are from props or user input
  const isUpdatingFromProps = useRef(false);

  // Sync local state with prop changes
  useEffect(() => {
    isUpdatingFromProps.current = true;
    setLocalAssignmentData(assignmentData);
    setDurationString(assignmentData.duration?.toString() ?? "");

    setTimeout(() => {
      isUpdatingFromProps.current = false;
    }, 0);
  }, [assignmentData]);

  // Update parent when local state changes (but only for user input changes)
  useEffect(() => {
    if (!isUpdatingFromProps.current) {
      onChange(localAssignmentData);
    }
  }, [localAssignmentData, onChange]);

  const handleDurationChange = (value: string) => {
    setDurationString(value);

    if (!value) {
      setLocalAssignmentData({
        ...localAssignmentData,
        duration: 0,
        endDate: localAssignmentData.startDate,
      });
      return;
    }

    const parsedDuration = parseInt(value, 10);

    if (isNaN(parsedDuration)) {
      return;
    }

    setLocalAssignmentData({
      ...localAssignmentData,
      duration: parsedDuration,
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-2">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t("assignments.generator.properties.name")}
          </label>
          <Input
            type="text"
            value={localAssignmentData.name}
            data-testid="assignment-name-input"
            onChange={(e) =>
              setLocalAssignmentData({
                ...localAssignmentData,
                name: e.target.value,
              })
            }
            classNames={{
              input:
                "w-full p-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600",
            }}
            placeholder={t("assignments.generator.properties.enterName")}
          />
        </div>
        {/* Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t("assignments.generator.properties.type")}
          </label>
          <div className="flex w-full gap-1 p-1">
            <Button
              rounded="lg"
              variant="outline"
              textColor={`${
                localAssignmentData.hwAssignmentType === "HOMEWORK" &&
                "text-indigo-700 dark:text-indigo-200"
              }`}
              fullWidth
              className={`p-3 col-span-4 ${
                localAssignmentData.hwAssignmentType === "HOMEWORK"
                  ? "border-indigo-500"
                  : "border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-400"
              }`}
              bgColor={`${
                localAssignmentData.hwAssignmentType === "HOMEWORK" &&
                "bg-indigo-50 dark:bg-indigo-900/50"
              }`}
              size="sm"
              onClick={() => {
                setLocalAssignmentData({
                  ...localAssignmentData,
                  hwAssignmentType: "HOMEWORK",
                  endDate: new Date(
                    new Date().getTime() + 2 * 24 * 60 * 60 * 1000,
                  ).toISOString(),
                });
              }}
            >
              {t("HOMEWORK")}
            </Button>
            <Button
              rounded="lg"
              variant="outline"
              textColor={`${
                localAssignmentData.hwAssignmentType === "QUIZ" &&
                "text-indigo-700 dark:text-indigo-200"
              }`}
              fullWidth
              className={`p-3 col-span-4 ${
                localAssignmentData.hwAssignmentType === "QUIZ"
                  ? "border-indigo-500"
                  : "border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-400"
              }`}
              bgColor={`${
                localAssignmentData.hwAssignmentType === "QUIZ" &&
                "bg-indigo-50 dark:bg-indigo-900/50"
              }`}
              size="sm"
              onClick={() => {
                setLocalAssignmentData({
                  ...localAssignmentData,
                  hwAssignmentType: "QUIZ",
                  endDate: undefined,
                });
              }}
            >
              {t("QUIZ")}
            </Button>
          </div>
        </div>

        {localAssignmentData.hwAssignmentType === "HOMEWORK" ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {t("assignments.generator.properties.endDate")}
            </label>
            <DateTimePicker
              value={localAssignmentData.endDate || ""}
              onChange={(date) =>
                setLocalAssignmentData({
                  ...localAssignmentData,
                  endDate: date,
                })
              }
              minDate={new Date()}
              className="w-full"
              placeholder={t("assignments.generator.properties.selectEndDate")}
            />
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t("assignments.generator.properties.duration")}{" "}
              {"(" + t("common.time.minutes") + ")"}
            </label>
            <Input
              type="text"
              value={durationString}
              onChange={(e) => handleDurationChange(e.target.value)}
              classNames={{
                input:
                  "w-full p-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600",
              }}
              placeholder={t("assignments.generator.properties.enterDuration")}
            />
            {isNaN(parseInt(durationString, 10)) && (
              <Text variant="p" className="text-xs mt-1 text-red-500">
                {t("assignments.generator.properties.invalidDuration")}
              </Text>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommonPropertiesStep;
